import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Benutzer } from '../types';
import RollenAuswahl from './RollenAuswahl';
import './BenutzerForm.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const ALLE_ROLLEN = [
  'Admin',
  'Geschäftsführer',
  'Lagerleiter',
  'Einkäufer',
  'Produktionsleiter',
  'Verkäufer',
  'Buchhaltung',
  'Projektleiter',
  'Projektmitarbeiter',
  'Personalverwaltung',
  'Arbeiter',
];

export interface EinladungsErgebnis {
  email: string;
  link: string;
  email_versendet: boolean;
}

export const registrierLink = (token: string) => `${window.location.origin}/registrieren?token=${token}`;

interface Props {
  editingBenutzer?: Benutzer | null;
  authToken: string;
  onSuccess: () => void;
  onCancel: () => void;
  /** Wird nach einer Einladung statt onSuccess gerufen (Ergebnisanzeige in der Liste). */
  onEinladung?: (ergebnis: EinladungsErgebnis) => void;
}

const BenutzerForm: React.FC<Props> = ({ editingBenutzer, authToken, onSuccess, onCancel, onEinladung }) => {
  const isEdit = !!editingBenutzer;
  const headers = { Authorization: `Bearer ${authToken}` };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    passwort: '',
    passwort_bestaetigung: '',
  });
  const [rollen, setRollen] = useState<string[]>(['Lagerleiter']);
  const [modus, setModus] = useState<'einladen' | 'passwort'>('einladen');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingBenutzer) {
      setFormData({
        name: editingBenutzer.name,
        email: editingBenutzer.email,
        passwort: '',
        passwort_bestaetigung: '',
      });
      setRollen(editingBenutzer.rollen?.length ? editingBenutzer.rollen : [editingBenutzer.rolle]);
    }
  }, [editingBenutzer]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validierung
    if (!formData.name.trim() || !formData.email.trim()) {
      setError('Name und E-Mail sind Pflichtfelder.');
      return;
    }
    if (rollen.length === 0) {
      setError('Mindestens eine Rolle ist erforderlich.');
      return;
    }
    if (!isEdit && modus === 'passwort' && !formData.passwort) {
      setError('Bitte ein Passwort vergeben.');
      return;
    }
    if (!isEdit && modus === 'passwort' && formData.passwort !== formData.passwort_bestaetigung) {
      setError('Passwörter stimmen nicht überein.');
      return;
    }
    if (!isEdit && modus === 'passwort' && formData.passwort.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen haben.');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        const payload: any = {
          name: formData.name,
          email: formData.email,
          rollen,
          rolle: rollen[0],
        };
        if (formData.passwort) {
          payload.passwort = formData.passwort;
        }
        await axios.put(
          `${API_URL}/api/benutzer/${editingBenutzer!.benutzer_id}`,
          payload,
          { headers }
        );
      } else if (modus === 'einladen') {
        const r = await axios.post(
          `${API_URL}/api/benutzer/einladen`,
          { name: formData.name, email: formData.email, rollen, rolle: rollen[0] },
          { headers }
        );
        if (onEinladung) {
          onEinladung({ email: formData.email, link: registrierLink(r.data.einladungs_token),
                        email_versendet: !!r.data.email_versendet });
          return;
        }
      } else {
        await axios.post(
          `${API_URL}/api/benutzer`,
          {
            name: formData.name,
            email: formData.email,
            rollen,
            rolle: rollen[0],
            passwort: formData.passwort,
          },
          { headers }
        );
      }
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="benutzer-form-container">
      <div className="benutzer-form-header">
        <h2 className="benutzer-form-title">
          {isEdit ? `Benutzer bearbeiten: ${editingBenutzer!.name}` : 'Neuer Benutzer'}
        </h2>
        <button className="btn-back" onClick={onCancel}>← Zurück</button>
      </div>

      <form onSubmit={handleSubmit} className="benutzer-form">
        <div className="form-group">
          <label>Name *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Vollständiger Name"
            required
          />
        </div>

        <div className="form-group">
          <label>E-Mail *</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="name@firma.de"
            required
          />
        </div>

        <div className="form-group">
          <label>Rollen *</label>
          <RollenAuswahl
            rollen={ALLE_ROLLEN}
            value={rollen}
            onChange={r => { setRollen(r); setError(''); }}
          />
        </div>

        {!isEdit && (
          <div className="form-group">
            <label>Zugang</label>
            <label style={{ display: 'block', fontWeight: 400 }}>
              <input type="radio" name="modus" checked={modus === 'einladen'} onChange={() => setModus('einladen')} />
              {' '}Einladung per E-Mail senden (empfohlen) – die Person legt ihr Passwort selbst fest
            </label>
            <label style={{ display: 'block', fontWeight: 400 }}>
              <input type="radio" name="modus" checked={modus === 'passwort'} onChange={() => setModus('passwort')} />
              {' '}Passwort selbst vergeben
            </label>
          </div>
        )}

        {(isEdit || modus === 'passwort') && (
          <>
            <div className="form-group">
              <label>{isEdit ? 'Neues Passwort (leer lassen = nicht ändern)' : 'Passwort *'}</label>
              <input
                type="password"
                name="passwort"
                value={formData.passwort}
                onChange={handleChange}
                placeholder={isEdit ? 'Leer lassen um beizubehalten' : 'Mindestens 6 Zeichen'}
              />
            </div>

            {(!isEdit || formData.passwort) && (
              <div className="form-group">
                <label>Passwort bestätigen {!isEdit && '*'}</label>
                <input
                  type="password"
                  name="passwort_bestaetigung"
                  value={formData.passwort_bestaetigung}
                  onChange={handleChange}
                  placeholder="Passwort wiederholen"
                />
              </div>
            )}
          </>
        )}

        {error && <div className="form-error">{error}</div>}

        <div className="form-actions">
          <button type="button" className="btn-cancel" onClick={onCancel}>
            Abbrechen
          </button>
          <button type="submit" className="btn-save" disabled={saving}>
            {saving ? 'Wird gespeichert...' : isEdit ? 'Speichern' : modus === 'einladen' ? 'Einladen' : 'Benutzer anlegen'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BenutzerForm;
